import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

import { Role, User } from "app/auth/models";
import { environment } from "environments/environment";
import { KeycloakService } from "keycloak-angular";
import { ToastrService } from "ngx-toastr";

@Injectable({ providedIn: "root" })
export class AuthenticationService {
  //public
  public currentUser: Observable<User>;

  //private
  private currentUserSubject: BehaviorSubject<User>;

  /**
   *
   * @param {HttpClient} _http
   * @param {ToastrService} _toastrService
   * @param {KeycloakService} _keycloak
   */
  constructor(
    private _http: HttpClient,
    private _toastrService: ToastrService,
    private _keycloak: KeycloakService,
  ) {
    this.currentUserSubject = new BehaviorSubject<User>(
      JSON.parse(localStorage.getItem("currentUser")),
    );
    this.currentUser = this.currentUserSubject.asObservable();
    this.initializeUser();
  }

  private async initializeUser() {
    try {
      const isLoggedIn = await this._keycloak.isLoggedIn();
      if (isLoggedIn) {
        this.userInfor().subscribe({
          next: (response: any) => {
            // console.log('response userInfo: ', response)
            if (response && response.result && response.data) {
              const userData = response.data;
              const userObj: User = {
                id: userData.id,
                iamId: userData.iamId,
                email: userData.email,
                username: userData.username,
                fullName: userData.fullName || userData.username,
                avatar: userData.avatar,
                role:
                  userData.roles && userData.roles.length > 0
                    ? userData.roles[0]
                    : "USER",
                roles: userData.roles || [],
                phoneNumber: userData.phoneNumber,
                gender: userData.gender,
                birthday: userData.birthday,
                fullAddress: userData.fullAddress,
                isActive: userData.isActive,
              };
              localStorage.setItem("currentUser", JSON.stringify(userObj));
              this.currentUserSubject.next(userObj);
            }
          },
          error: (err) => {
            console.error("Failed to load user info:", err);
          },
        });
      }
    } catch (e) {
      console.error("Error checking keycloak login status:", e);
    }
  }

  // getter: currentUserValue
  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  /**
   *  Confirms if user is admin
   */
  get isAdmin(): boolean {
    const u = this.currentUserSubject.value;
    if (!u) return false;
    // Khớp BE @PreAuthorize: admin / ADMIN / TENANT_ADMIN / TENANT_SUPERADMIN / SUPER_ADMIN (+ Role.Admin).
    // Xét TẤT CẢ roles (không chỉ roles[0]); so sánh không phân biệt hoa/thường.
    const adminRoles = [
      "admin", "administrator", "tenant_admin", "tenant_superadmin", "super_admin",
    ];
    const roles = u.roles && u.roles.length ? u.roles : u.role ? [String(u.role)] : [];
    return roles.some((r) => adminRoles.includes(String(r).toLowerCase()));
  }

  /**
   *  Confirms if user is client
   */
  get isClient() {
    return (
      this.currentUser &&
      this.currentUserSubject.value &&
      this.currentUserSubject.value.role === Role.Client
    );
  }

  /**
   * User login
   *
   * @param email
   * @param password
   * @returns user
   */
  userInfor() {
    return this._http.get(`${environment.apiUrl}/user/userinfo`);
  }

  /**
   * Update current user personal profile
   */
  updateSelfProfile(body: {
    fullName: string;
    gender: string;
    birthday: string;
    phoneNumber: string;
    fullAddress: string;
  }) {
    return this._http.put(`${environment.apiUrl}/user/selfupdate`, body);
  }

  /**
   * Update local currentUser state in subject & localStorage
   */
  updateCurrentUserValue(updatedFields: Partial<User>) {
    const updatedUser = { ...this.currentUserValue, ...updatedFields };
    localStorage.setItem("currentUser", JSON.stringify(updatedUser));
    this.currentUserSubject.next(updatedUser);
  }

  /**
   * User logout
   *
   */
  logout() {
    // remove user from local storage to log user out
    localStorage.removeItem("currentUser");
    // notify
    this.currentUserSubject.next(null);
    // Keycloak logout
    this._keycloak.logout(window.location.origin);
  }
}
